import { Alert, AlertTitle, Box, Chip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';

/**
 * BetaTestBanner — sichtbarer Hinweis dass sich ImmoKompass in einer
 * Testphase befindet und KI-Analysen unverbindlich sind.
 *
 * Zwei Varianten:
 *   - "compact": kleiner Chip mit „Beta · Keine Gewähr". Für Headers,
 *     Hero-Sections, etc. wo wenig Platz ist.
 *   - "full" (default): Alert mit Titel + ausführlichem Text. Für Stellen
 *     wo der User vor einer Aktion (Analyse starten, Paket kaufen,
 *     Account erstellen) gewarnt werden soll.
 *
 * Tieferer Haftungsausschluss nach erfolgter Analyse: siehe
 * AnalysisDisclaimer-Komponente.
 */
type BetaTestBannerProps = {
  variant?: 'compact' | 'full';
  sx?: SxProps<Theme>;
};

export function BetaTestBanner({ variant = 'full', sx }: BetaTestBannerProps) {
  if (variant === 'compact') {
    return (
      <Box sx={sx}>
        <Chip
          icon={<ScienceOutlinedIcon />}
          label="Beta · KI-Analyse ohne Gewähr"
          color="warning"
          variant="outlined"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      </Box>
    );
  }

  return (
    <Alert
      severity="warning"
      icon={<ScienceOutlinedIcon fontSize="inherit" />}
      sx={{
        border: '1px solid',
        borderColor: 'warning.main',
        borderRadius: 2,
        ...sx,
      }}
    >
      <AlertTitle sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 0.5 }}>
        Testphase — ImmoKompass befindet sich noch in der Beta
      </AlertTitle>
      <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
        Die KI-Analysen sind <strong>unverbindliche Einschätzungen</strong> und können
        unvollständig oder fehlerhaft sein. ImmoKompass <strong>übernimmt keine Haftung</strong>{' '}
        für inhaltliche Fehler oder Schlussfolgerungen der KI. Die Analyse ersetzt
        keine fachkundige Prüfung durch Sachverständige, Notare oder Fachanwälte.
        Kaufentscheidungen treffe immer auf Basis professioneller Beratung — niemals
        allein aufgrund einer ImmoKompass-Analyse.
      </Typography>
    </Alert>
  );
}
